'use client'

import {
    CommissionEntry,
    CommissionMode,
    CommissionRequest,
    CommissionResult,
    CommissionSettings,
    ReportFilters,
    downloadCommissionStatement,
    getCommissionSettings,
    previewCommission,
} from '@/lib/api/reports/reports-api'
import { createRef, useEffect, useMemo, useState } from 'react'
import { MdDownload, MdOpenInNew, MdTune, MdWarning } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { formatCurrency, ProductivityType } from '../config/report-configs'
import CommissionItemsPicker from './commission-items-picker'

/** Um tipo de atendimento selecionado, com a base que ele contribui para a comissão. */
export interface CommissionLine {
    key: string
    /** Rótulo no plural ("consultas", "exames"). */
    label: string
    /** Registros do filtro corrente daquele tipo. */
    total: number
    /** Receita apurada do filtro corrente daquele tipo, em centavos. */
    revenueCents: number
}

/** Um tipo e o filtro efetivo com que ele foi apurado. */
interface CommissionEntryInput {
    type: ProductivityType
    label: string
    filters: Partial<ReportFilters>
}

interface CommissionModalProps {
    isOpen: boolean
    onClose: () => void
    /** Uma linha por tipo selecionado. Com uma só, o modal some a divisão por tipo. */
    lines: CommissionLine[]
    /** Usuário apurado. É por ele que o servidor busca a taxa cadastrada. */
    userId: number | null
    /** Nome do usuário a quem a comissão se refere. */
    userName: string
    /** Tipos e filtros efetivos — é o que o servidor recalcula e o PDF documenta. */
    entries: CommissionEntryInput[]
    /** Período apurado, para o cabeçalho do demonstrativo. */
    periodLabel?: string
}

/** O que a base de uma linha percentual representa, para ler "R$ X de markup x 10%". */
const BASIS_LABELS: Record<CommissionResult['lines'][number]['basis'], string> = {
    REVENUE: 'de receita',
    MARGIN: 'de markup',
    mixed: 'de base (receita ou markup, conforme a unidade)',
}

function formatPercent(basisPoints: number): string {
    return `${(basisPoints / 100).toLocaleString('pt-BR')}%`
}

/**
 * Comissão por produtividade sobre os tipos selecionados na aba.
 *
 * **A taxa NÃO é digitada aqui.** Ela vem do cadastro do usuário (Usuários > o usuário
 * > Comissão por produtividade) e este modal só a exibe. Digitada a cada apuração, nada
 * ficava registrado sobre o que fora combinado: duas pessoas apurando o mesmo mês
 * chegavam a valores diferentes e nada denunciava. Alterar exige editar o cadastro, o
 * que deixa rastro de quem mudou e quando.
 *
 * O modo de cálculo — **valor fixo** (por atendimento) ou **percentual** (sobre a
 * receita apurada) — e as taxas por tipo também são do cadastro.
 *
 * **Quem calcula é o servidor, atendimento a atendimento.** O total é a soma das linhas
 * que o demonstrativo imprime — se a tela fizesse a conta por cima dos agregados, o PDF
 * mostraria uma coluna que não fecha com o total, e é por essa coluna que quem recebe
 * confere. É também o que permite tirar um atendimento da conta sem refazer rateio
 * nenhum.
 *
 * **Não gera lançamento.** Comissão de CLT é rubrica de folha; de autônomo, conta a
 * pagar. Qual vale depende do contrato de quem recebe, então aqui sai a memória de
 * cálculo e o demonstrativo — quem lança é o financeiro, no lugar certo.
 */
export default function CommissionModal({
    isOpen,
    onClose,
    lines,
    userId,
    userName,
    entries,
    periodLabel,
}: CommissionModalProps) {
    const modalRef = createRef<HTMLDialogElement>()
    const navigate = useNavigate()
    /** A regra cadastrada. `null` enquanto carrega ou quando não há nenhuma. */
    const [settings, setSettings] = useState<CommissionSettings | null>(null)
    const [loadingSettings, setLoadingSettings] = useState(false)
    /** Por que não há taxa: a mensagem do servidor, exibida no lugar do cálculo. */
    const [settingsError, setSettingsError] = useState<string | null>(null)
    /** Atendimentos fora do cálculo, por tipo. Vale só para esta apuração. */
    const [excluded, setExcluded] = useState<Record<string, Set<number>>>({})
    const [adjusting, setAdjusting] = useState(false)
    const [result, setResult] = useState<CommissionResult | null>(null)
    const [calculating, setCalculating] = useState(false)
    const [downloading, setDownloading] = useState(false)

    /** Critério GERAL — o dos tipos que não têm o seu. */
    const mode: CommissionMode = settings?.mode ?? 'fixed'

    useEffect(() => {
        if (isOpen) modalRef.current?.showModal()
        else modalRef.current?.close()
    }, [isOpen])

    /**
     * Reabrir recomeça o fluxo e **relê a taxa do cadastro** — alterada no meio do
     * caminho, o modal aberto continuaria calculando com a antiga.
     */
    useEffect(() => {
        if (!isOpen) return
        setExcluded({})
        setAdjusting(false)
        setResult(null)
        setSettings(null)
        setSettingsError(null)
        if (!userId) return

        setLoadingSettings(true)
        getCommissionSettings(userId)
            .then(res => {
                if (res.success && res.result) setSettings(res.result)
                else
                    setSettingsError(
                        res.error ||
                            'Este usuário não tem comissão configurada no cadastro.'
                    )
            })
            .catch(() =>
                setSettingsError('Não foi possível ler a comissão cadastrada.')
            )
            .finally(() => setLoadingSettings(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, userId])

    const totals = useMemo(
        () => ({
            count: lines.reduce((sum, line) => sum + line.total, 0),
            revenueCents: lines.reduce((sum, line) => sum + line.revenueCents, 0),
        }),
        [lines]
    )

    const excludedCount = Object.values(excluded).reduce(
        (sum, set) => sum + set.size,
        0
    )

    /** O que está cadastrado para aquele tipo: critério e taxa, já resolvidos. */
    const settingsOf = (key: string) =>
        settings?.byType[key as keyof CommissionSettings['byType']]

    /** Taxa cadastrada daquele tipo — centavos (fixo) ou basis points (percentual). */
    const rateOf = (key: string): number => settingsOf(key)?.rate ?? 0

    /** Critério daquele tipo. Pode diferir do geral: acordo misto é caso real. */
    const modeOf = (key: string): CommissionMode =>
        settingsOf(key)?.mode ?? mode

    /**
     * Há base para calcular? A pergunta é POR TIPO, porque o critério é por tipo: um
     * acordo que paga percentual só sobre exames não depende de haver receita nas
     * consultas.
     */
    const typeHasBase = (line: CommissionLine): boolean =>
        modeOf(line.key) === 'percentage'
            ? line.revenueCents > 0
            : line.total > 0

    const toggleExcluded = (type: string, id: number) => {
        setExcluded(prev => {
            const next = new Set(prev[type] ?? [])
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return { ...prev, [type]: next }
        })
        // A conta muda: o resultado que está na tela deixou de valer.
        setResult(null)
    }

    /**
     * O corpo que o servidor recalcula — o mesmo do preview e do demonstrativo.
     *
     * **Sem taxa e sem modo**: os dois saem do cadastro do usuário, no servidor. O
     * `userId` é o que ele usa para encontrá-los.
     */
    const buildRequest = (): CommissionRequest => ({
        userId: userId ?? 0,
        recipientName: userName,
        periodLabel,
        entries: entries.map<CommissionEntry>(entry => {
            const ids = Array.from(excluded[entry.type] ?? [])
            return {
                type: entry.type,
                filters: {
                    ...(entry.filters as ReportFilters),
                    ...(ids.length ? { excludeIds: ids } : {}),
                },
            }
        }),
    })

    // Basta UM tipo com taxa e base para haver o que calcular.
    const hasBase = lines.some(line => rateOf(line.key) > 0 && typeHasBase(line))
    const canContinue = !!settings && hasBase && !calculating

    const calculate = async () => {
        setCalculating(true)
        try {
            const res = await previewCommission(buildRequest())
            if (res.success && res.result) setResult(res.result)
            else toast.error(res.error || 'Não foi possível calcular a comissão')
        } catch {
            toast.error('Não foi possível calcular a comissão')
        } finally {
            setCalculating(false)
        }
    }

    const download = async () => {
        setDownloading(true)
        try {
            await downloadCommissionStatement(buildRequest())
        } catch {
            toast.error('Não foi possível gerar o demonstrativo')
        } finally {
            setDownloading(false)
        }
    }

    /** A taxa no critério DELA — num acordo misto, cada linha lê o seu. */
    const formatRateFor = (rateMode: CommissionMode, rate: number) =>
        rateMode === 'percentage' ? formatPercent(rate) : formatCurrency(rate)

    return (
        <dialog className="modal z-50" ref={modalRef}>
            <div className="modal-box !max-w-3xl">
                <h2 className="text-lg font-bold text-base-content">Calcular comissão</h2>
                <p className="mt-1 text-sm text-base-content/60">
                    {userName} tem{' '}
                    <span className="font-semibold">{totals.count}</span> registro
                    {totals.count === 1 ? '' : 's'} no período filtrado, somando{' '}
                    <span className="font-semibold">
                        {formatCurrency(totals.revenueCents)}
                    </span>{' '}
                    em receita apurada.
                </p>

                {/* A base é o que o filtro da aba produziu, tipo a tipo: sem isto o
                    operador vê um total e não sabe de qual recorte ele saiu. */}
                <div className="mt-3 rounded-lg border border-base-300 divide-y divide-base-300">
                    {lines.map(line => (
                        <div
                            key={line.key}
                            className="px-3 py-2 flex items-center justify-between gap-3 text-xs"
                        >
                            <span className="capitalize text-base-content">
                                {line.label}
                            </span>
                            <span className="text-base-content/60">
                                {line.total} atendimento
                                {line.total === 1 ? '' : 's'} ·{' '}
                                {formatCurrency(line.revenueCents)} de receita
                                {excluded[line.key]?.size ? (
                                    <span className="text-warning ml-1">
                                        ({excluded[line.key].size} fora do cálculo)
                                    </span>
                                ) : null}
                            </span>
                        </div>
                    ))}
                </div>

                {/* A taxa vem do CADASTRO do usuário e não é editável aqui — ver o
                    comentário do componente. Mostrá-la é o que permite conferir antes
                    de calcular; alterá-la exige abrir o cadastro, que registra quem
                    mudou. */}
                <div className="mt-5 rounded-xl border border-base-300 bg-base-200/40 p-4 flex flex-col gap-3">
                    {loadingSettings ? (
                        <span className="loading loading-spinner loading-sm text-secondary" />
                    ) : settingsError ? (
                        <div className="flex items-start gap-3">
                            <MdWarning className="text-xl text-warning shrink-0 mt-0.5" />
                            <div className="text-sm">
                                <p className="font-medium text-base-content">
                                    {settingsError}
                                </p>
                                <p className="text-xs text-base-content/60 mt-1">
                                    A taxa deixou de ser digitada na apuração: ela é
                                    cadastro, para ficar registrado o que foi combinado.
                                </p>
                                {userId && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-secondary gap-2 mt-3"
                                        onClick={() =>
                                            navigate(`/security/users/edit/${userId}`)
                                        }
                                    >
                                        <MdOpenInNew />
                                        Cadastrar comissão
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : settings ? (
                        <>
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div>
                                    <span className="text-xs uppercase tracking-wide text-base-content/50 font-medium">
                                        Comissão cadastrada
                                    </span>
                                    <p className="text-base font-semibold text-base-content">
                                        {/* Com critérios diferentes por tipo, o
                                            cabeçalho não afirma um só — quem manda é a
                                            linha de cada tipo, logo abaixo. */}
                                        {lines.every(
                                            line =>
                                                modeOf(line.key) === modeOf(lines[0].key)
                                        )
                                            ? modeOf(lines[0]?.key ?? '') ===
                                              'percentage'
                                                ? settings.basis === 'MARGIN'
                                                    ? 'Percentual sobre o markup'
                                                    : 'Percentual sobre a receita'
                                                : 'Valor fixo por atendimento'
                                            : 'Critério varia por tipo'}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-xs gap-1"
                                    onClick={() =>
                                        navigate(`/security/users/edit/${userId}`)
                                    }
                                >
                                    <MdOpenInNew />
                                    Alterar no cadastro
                                </button>
                            </div>

                            <div className="rounded-lg border border-base-300 divide-y divide-base-300 bg-base-100">
                                {lines.map(line => (
                                    <div
                                        key={line.key}
                                        className="px-3 py-2 flex items-center justify-between gap-3 text-sm"
                                    >
                                        <span className="capitalize text-base-content">
                                            {line.label}
                                        </span>
                                        <span
                                            className={
                                                rateOf(line.key) > 0
                                                    ? 'font-semibold text-secondary'
                                                    : 'text-base-content/50 text-xs'
                                            }
                                        >
                                            {rateOf(line.key) > 0
                                                ? formatRateFor(
                                                      modeOf(line.key),
                                                      rateOf(line.key)
                                                  )
                                                : 'sem taxa — não comissiona'}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            {settings.updatedAt && (
                                <span className="text-xs text-base-content/50">
                                    Cadastrada em{' '}
                                    {new Date(settings.updatedAt).toLocaleDateString(
                                        'pt-BR'
                                    )}
                                    {settings.updatedBy
                                        ? ` por ${settings.updatedBy.name}`
                                        : ''}
                                </span>
                            )}
                        </>
                    ) : null}
                </div>

                <div className="mt-5 flex flex-col gap-4">

                    {settings && !hasBase && (
                        <span className="text-xs text-warning">
                            Nenhum tipo selecionado tem taxa cadastrada e base no
                            período: o percentual precisa de receita apurada, e o valor
                            fixo, de atendimentos.
                        </span>
                    )}

                    {/* Tirar um atendimento da conta sem apertar o filtro até a linha
                        sumir — o que levaria outras junto. Nada é marcado no registro:
                        ele volta inteiro na próxima apuração. */}
                    <div>
                        <button
                            type="button"
                            className="btn btn-ghost btn-sm gap-2"
                            onClick={() => setAdjusting(!adjusting)}
                        >
                            <MdTune className="text-lg" />
                            {adjusting ? 'Ocultar atendimentos' : 'Escolher atendimentos'}
                            {excludedCount > 0 && (
                                <span className="badge badge-warning badge-sm">
                                    {excludedCount} fora
                                </span>
                            )}
                        </button>

                        {adjusting && (
                            <div className="mt-3 flex flex-col gap-3">
                                <p className="text-xs text-base-content/50">
                                    Desmarque o que não deve ser comissionado. Vale só
                                    para este cálculo: nada é alterado no atendimento.
                                </p>
                                {entries.map(entry => (
                                    <CommissionItemsPicker
                                        key={entry.type}
                                        type={entry.type}
                                        label={entry.label}
                                        filters={entry.filters}
                                        excluded={excluded[entry.type] ?? new Set()}
                                        onToggle={id => toggleExcluded(entry.type, id)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    {result && (
                        <div className="rounded-xl bg-base-200 p-4 flex flex-col gap-3">
                            {result.truncated && (
                                <div className="alert alert-warning text-xs">
                                    <span>
                                        O período tem mais atendimentos do que este
                                        cálculo consegue somar de uma vez. Apure por
                                        intervalos menores antes de pagar.
                                    </span>
                                </div>
                            )}
                            {result.costUnknownCount > 0 && (
                                <div className="alert alert-warning text-xs">
                                    <span>
                                        {result.costUnknownCount} atendimento
                                        {result.costUnknownCount === 1 ? '' : 's'} sem
                                        repasse apurado ficaram sem comissão: a base é o
                                        markup, e sem o custo não há como apurá-lo.
                                        Apure o repasse e recalcule antes de pagar.
                                    </span>
                                </div>
                            )}
                            {result.lines.map(line => (
                                <div
                                    key={line.type}
                                    className="flex items-center justify-between text-sm gap-3"
                                >
                                    <span className="text-base-content/60 capitalize">
                                        {line.label}
                                    </span>
                                    {/* A base exibida é a do critério DAQUELA linha:
                                        receita onde é percentual, contagem onde é
                                        valor fixo. Num acordo misto, usar o critério
                                        geral aqui mostraria a conta errada. */}
                                    <span className="text-base-content/60 text-xs text-right grow">
                                        {line.mode === 'percentage'
                                            ? `${formatCurrency(line.baseCents)} ${BASIS_LABELS[line.basis]}`
                                            : `${line.count} atendimento${line.count === 1 ? '' : 's'}`}{' '}
                                        x {formatRateFor(line.mode, line.rate)}
                                    </span>
                                    <span className="font-medium text-base-content">
                                        {formatCurrency(line.commissionCents)}
                                    </span>
                                </div>
                            ))}
                            <div className="border-t border-base-300 pt-3 flex items-center justify-between">
                                <span className="font-semibold text-base-content">
                                    Comissão
                                </span>
                                <span className="text-xl font-bold text-secondary">
                                    {formatCurrency(result.totalCommissionCents)}
                                </span>
                            </div>
                            <p className="text-xs text-base-content/50">
                                Somada atendimento a atendimento — é a mesma conta que o
                                demonstrativo detalha, linha por linha.
                            </p>
                        </div>
                    )}
                </div>

                <div className="modal-action">
                    <button type="button" className="btn btn-ghost" onClick={onClose}>
                        Fechar
                    </button>
                    {result && (
                        <button
                            type="button"
                            className="btn btn-ghost border border-base-300 gap-2"
                            disabled={downloading}
                            onClick={download}
                        >
                            {downloading ? (
                                <span className="loading loading-spinner loading-xs" />
                            ) : (
                                <MdDownload className="text-lg" />
                            )}
                            Demonstrativo em PDF
                        </button>
                    )}
                    <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={!canContinue}
                        onClick={calculate}
                    >
                        {calculating ? (
                            <span className="loading loading-spinner loading-xs" />
                        ) : result ? (
                            'Recalcular'
                        ) : (
                            'Calcular'
                        )}
                    </button>
                </div>
            </div>

            <form method="dialog" className="modal-backdrop">
                <button type="submit" onClick={onClose}>
                    Fechar
                </button>
            </form>
        </dialog>
    )
}
